import { Fragment, useState } from 'react'
import { Button, LinkButton, Modal, Step, Text, ToastHelper } from '$components'
import { CopyIcon, InboxIcon, InfoIcon } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Config } from '$config/app'
import { Router } from '$utils/router'

export function NoAppointmentRequestsEmptyState() {
  const { bookingUrl, isBookingEnabled } = useTenant()
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false)

  const fullBookingUrl = bookingUrl ? Config.getBookingPageUrl(bookingUrl) : null
  const canCopyBookingLink = Boolean(isBookingEnabled && fullBookingUrl)

  const handleCopyBookingLink = async () => {
    if (!fullBookingUrl) {
      return
    }

    try {
      await navigator.clipboard.writeText(fullBookingUrl)
      ToastHelper.success('Booking link copied')
    } catch {
      ToastHelper.error('Failed to copy booking link')
    }
  }

  return (
    <Fragment>
      <div className="w-full">
        <div className="w-full h-fit p-12 bg-neutral-50 rounded-3xl flex flex-col items-center text-center">
          <InboxIcon className="size-12 text-indigo-500 mb-4" />

          <Text
            type="h3"
            className="mb-4 text-gray-900"
          >
            No appointment requests yet
          </Text>

          <Text
            type="s"
            className="text-gray-600 max-w-md"
          >
            When clients book online, their requests appear here for you to review and confirm.
          </Text>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <LinkButton to={Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsOnlineBooking)}>
              Enable Online Booking
            </LinkButton>

            {canCopyBookingLink && (
              <Button
                variant="secondary"
                icon={CopyIcon}
                onClick={handleCopyBookingLink}
              >
                Copy Booking Link
              </Button>
            )}
          </div>

          <Button
            variant="ghost"
            icon={InfoIcon}
            className="mt-5 w-fit"
            onClick={() => setIsHowItWorksModalOpen(true)}
          >
            How do appointment requests work?
          </Button>
        </div>
      </div>

      {isHowItWorksModalOpen && (
        <Modal onClose={() => setIsHowItWorksModalOpen(false)}>
          <Modal.Header>
            <Text
              type="h2"
              as="h1"
            >
              How appointment requests work
            </Text>

            <Text
              type="s"
              className="text-gray-500"
            >
              Online requests need your review before they are added to the calendar.
            </Text>
          </Modal.Header>

          <Modal.Content>
            <div className="space-y-2">
              <Step
                step={1}
                title="Turn on Online Booking"
                description="Enable online booking in settings."
              />

              <Step
                step={2}
                title="Share your booking link"
                description="Send it to clients or add it to your website or Facebook page."
              />

              <Step
                step={3}
                title="Confirm requests"
                description="When a client books, the request appears here. Confirm it to add the appointment to your calendar."
                withLine={false}
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
    </Fragment>
  )
}
